import type {
  CreateSocialPostRequest,
  Product,
  SocialPost,
  SocialProvider,
  UpdateSocialPostRequest,
} from '@nucleus-commerce/api-client/admin';

import { stripHtml, truncateWords } from '@/features/seo';

import {
  PINTEREST_LIMITS,
  type PostFormInput,
  type PostFormOutput,
} from '../schemas/post-form-schema';

export function emptyPostForm(): PostFormInput {
  return {
    productIds: [],
    variants: [],
    mediaIds: [],
    target: null,
    title: '',
    description: '',
    altText: '',
    link: '',
  };
}

export function fromSocialPost(post: SocialPost): PostFormInput {
  const products = [...post.products].sort((a, b) => a.position - b.position);
  return {
    productIds: Array.from(new Set(products.map((p) => p.productId))),
    variants: products.flatMap((p) =>
      p.variantId ? [{ variantId: p.variantId, productId: p.productId, title: null }] : [],
    ),
    mediaIds: post.media.map((m) => m.mediaId),
    target: post.target
      ? {
          boardId: post.target.boardId,
          boardName: post.target.boardName,
          boardSectionId: post.target.boardSectionId ?? null,
        }
      : null,
    title: post.title ?? '',
    description: post.description ?? '',
    altText: post.altText ?? '',
    link: post.link ?? '',
  };
}

const text = (value: string): string | undefined => (value === '' ? undefined : value);

/**
 * A new draft. Empty text fields are left out and `prefill: true` lets the backend fill them
 * from the first product, including the link on the store's primary domain.
 */
export function toCreatePostBody(
  values: PostFormOutput,
  provider: SocialProvider,
): CreateSocialPostRequest {
  const title = text(values.title);
  const description = text(values.description);
  const altText = text(values.altText);
  const link = text(values.link);
  return {
    provider,
    productIds: values.productIds,
    variantIds: values.variants.map((v) => v.variantId),
    ...(values.mediaIds.length > 0 ? { mediaIds: values.mediaIds } : {}),
    target: values.target,
    ...(title ? { title } : {}),
    ...(description ? { description } : {}),
    ...(altText ? { altText } : {}),
    ...(link ? { link } : {}),
    prefill: true,
  };
}

const same = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((value, i) => value === b[i]);

/**
 * Only what changed, so a scheduled post keeps its time and an unchanged image list does not
 * reset the renditions. A cleared text field is sent as null.
 */
export function toUpdatePostBody(
  values: PostFormOutput,
  saved: PostFormInput,
): UpdateSocialPostRequest {
  const body: UpdateSocialPostRequest = {};
  if (!same(values.productIds, saved.productIds)) body.productIds = values.productIds;
  const variantIds = values.variants.map((v) => v.variantId);
  if (
    !same(
      variantIds,
      saved.variants.map((v) => v.variantId),
    )
  )
    body.variantIds = variantIds;
  if (!same(values.mediaIds, saved.mediaIds) && values.mediaIds.length > 0) {
    body.mediaIds = values.mediaIds;
  }
  const target = values.target;
  const savedTarget = saved.target;
  if (
    (target === null) !== (savedTarget === null) ||
    (target &&
      savedTarget &&
      (target.boardId !== savedTarget.boardId ||
        target.boardSectionId !== savedTarget.boardSectionId))
  ) {
    body.target = target;
  }
  for (const key of ['title', 'description', 'altText', 'link'] as const) {
    if (values[key] !== saved[key]) body[key] = values[key] === '' ? null : values[key];
  }
  return body;
}

const firstNonEmpty = (values: ReadonlyArray<string | null | undefined>): string =>
  values.map((value) => value?.trim() ?? '').find((value) => value !== '') ?? '';

/**
 * What "Fill from product" writes: the editor's own copy of the backend's `prefill` rules so the
 * words appear in the fields before the first save. The link is the product page on the primary
 * domain; without one the field stays as it is.
 */
export function fillFromProduct(
  product: Pick<Product, 'title' | 'handle' | 'descriptionHtml' | 'summary' | 'seo'>,
  baseUrl: string | null,
  variantId: string | null,
): Pick<PostFormInput, 'title' | 'description' | 'altText'> & { link: string | null } {
  const title = truncateWords(product.title.trim(), PINTEREST_LIMITS.title);
  const description = truncateWords(
    firstNonEmpty([
      product.seo?.description,
      stripHtml(product.descriptionHtml ?? ''),
      product.summary,
    ]),
    PINTEREST_LIMITS.description,
  );
  const link = baseUrl
    ? `${baseUrl}/products/${product.handle}${variantId ? `?variant=${variantId}` : ''}`
    : null;
  return { title, description, altText: truncateWords(title, PINTEREST_LIMITS.altText), link };
}

/** The link that goes out: the stored bare link plus the UTM the backend appends on publish. */
export function publishedLink(link: string, postId: string): string {
  const url = new URL(link);
  url.searchParams.set('utm_source', 'pinterest');
  url.searchParams.set('utm_medium', 'social');
  url.searchParams.set('utm_campaign', postId);
  return url.toString();
}
